<template>
    <div :class="alertClasses" role="alert">
        <div class="flex items-center gap-3" :class="{ 'justify-center': center }">
            <div class="flex-shrink-0">
                <fa :icon="['fas', icon]" :spin="spin" :class="iconClasses" />
            </div>
            <div class="min-w-0" :class="{ 'flex-1': !center }">
                <p :class="titleClasses">
                    <t :path="title" />
                </p>
                <ul v-if="messages && messages.length" class="mt-1 space-y-0.5">
                    <li v-for="(message, idx) of messages" :key="idx" class="text-sm opacity-75">• <t :path="message" /></li>
                </ul>
            </div>
            <button v-if="dismissible" @click="$emit('dismiss')" :class="dismissButtonClasses" type="button" aria-label="Dismiss">
                <fa :icon="['fas', 'times']" class="text-sm" />
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

export default defineComponent({
    emits: ['dismiss'],
    props: {
        type: {
            required: true,
            type: String,
            validator: (value: string) => ['info', 'danger', 'warning', 'success', 'primary', 'accent', 'inherit'].includes(value),
        },
        icon: {
            required: true,
            type: String,
        },
        spin: {
            type: Boolean,
            default: false,
        },
        title: {
            required: true,
            type: [String, Array],
        },
        messages: {
            type: Array,
        },
        dismissible: {
            type: Boolean,
            default: false,
        },
        center: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const alertClasses = computed(() => [
            'rounded-lg border transition-all duration-200 px-3 py-2',
            {
                'bg-info-500/10 border-info-500/30 text-info-100': props.type === 'info',
                'bg-danger-500/10 border-danger-500/30 text-danger-100': props.type === 'danger',
                'bg-warning-500/10 border-warning-500/30 text-warning-100': props.type === 'warning',
                'bg-success-500/10 border-success-500/30 text-success-100': props.type === 'success',
                'bg-primary-500/10 border-primary-500/30 text-primary-100': props.type === 'primary',
                'bg-accent-500/10 border-accent-500/30 text-accent-100': props.type === 'accent',
            },
        ]);

        const iconClasses = computed(() => [
            'text-lg',
            {
                'text-info-400': props.type === 'info',
                'text-danger-400': props.type === 'danger',
                'text-warning-400': props.type === 'warning',
                'text-success-400': props.type === 'success',
                'text-primary-400': props.type === 'primary',
                'text-accent-400': props.type === 'accent',
            },
        ]);

        const titleClasses = computed(() => [
            'font-medium text-sm',
            {
                'text-info-200': props.type === 'info',
                'text-danger-200': props.type === 'danger',
                'text-warning-200': props.type === 'warning',
                'text-success-200': props.type === 'success',
                'text-primary-200': props.type === 'primary',
                'text-accent-200': props.type === 'accent',
            },
        ]);

        const dismissButtonClasses = computed(() => [
            'flex-shrink-0 rounded-md p-1.5 transition-colors hover:bg-primary-50/10 focus:outline-none focus:ring-2 focus:ring-white/20',
            {
                'text-info-300 hover:text-info-200': props.type === 'info',
                'text-danger-300 hover:text-danger-200': props.type === 'danger',
                'text-warning-300 hover:text-warning-200': props.type === 'warning',
                'text-success-300 hover:text-success-200': props.type === 'success',
                'text-primary-300 hover:text-primary-200': props.type === 'primary',
                'text-accent-300 hover:text-accent-200': props.type === 'accent',
            },
        ]);

        return {
            alertClasses,
            iconClasses,
            titleClasses,
            dismissButtonClasses,
        };
    },
});
</script>
